import React, {useState, useEffect, useCallback} from "react";
import {useAuth} from "../../AuthContext";
import {SystemLogsService} from "../../services/api/systemLogsService";
import {convertToDate} from "../../utils/dateUtils";

interface SystemLog {
  id: string;
  collection: string;
  documentId: string;
  activity: any;
  timestamp: any;
  type: string;
  description: string;
  details: any;
  userId?: string;
  characterId?: string;
  dmId?: string;
  userName?: string;
  userEmail?: string;
  characterName?: string;
  playerName?: string;
}

interface LogFilters {
  logTypes: string[];
  startDate: string;
  endDate: string;
  userId: string;
  characterId: string;
  limit: number;
}

const logTypeOptions = [
  {value: "playerActivity", label: "Player Activity"},
  {value: "characterActivity", label: "Character Activity"},
  {value: "dmActivity", label: "DM Activity"},
  {value: "inventoryActivity", label: "Inventory Activity"},
];

const SystemLogs: React.FC = () => {
  const {user} = useAuth();
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalLogs, setTotalLogs] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  const [filters, setFilters] = useState<LogFilters>({
    logTypes: [
      "playerActivity",
      "characterActivity",
      "dmActivity",
      "inventoryActivity",
    ],
    startDate: "",
    endDate: "",
    userId: "",
    characterId: "",
    limit: 50,
  });

  const loadLogs = useCallback(
    async (resetOffset = true) => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        const currentOffset = resetOffset ? 0 : offset;

        const response = await SystemLogsService.getSystemLogs({
          logTypes: filters.logTypes,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          userId: filters.userId || undefined,
          characterId: filters.characterId || undefined,
          limit: filters.limit,
          offset: currentOffset,
        });

        if (resetOffset) {
          setLogs(response.logs);
          setOffset(0);
        } else {
          setLogs((prev) => [...prev, ...response.logs]);
          setOffset(currentOffset + response.logs.length);
        }
        setTotalLogs(response.total);
        setHasMore(response.hasMore);
      } catch (err: any) {
        // Error loading logs - handled by error state
        setError(err.message || "Failed to load logs");
      } finally {
        setLoading(false);
      }
    },
    [user, filters, offset]
  );

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = (field: keyof LogFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLogTypeToggle = (logType: string) => {
    setFilters((prev) => ({
      ...prev,
      logTypes: prev.logTypes.includes(logType)
        ? prev.logTypes.filter((t) => t !== logType)
        : [...prev.logTypes, logType],
    }));
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadLogs(false);
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return "N/A";

    const date = convertToDate(timestamp);
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getLogTypeLabel = (collection: string) => {
    const option = logTypeOptions.find((opt) => opt.value === collection);
    return option?.label || collection;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "created":
      case "account_created":
        return "🆕";
      case "updated":
      case "profile_updated":
        return "✏️";
      case "deleted":
      case "account_deleted":
        return "🗑️";
      case "level_up":
        return "⬆️";
      case "experience_gain":
      case "experience_awarded":
        return "⭐";
      case "session_logged":
        return "📝";
      case "item_added":
        return "➕";
      case "item_removed":
        return "➖";
      case "item_updated":
        return "🔄";
      case "role_added":
      case "role_assigned":
        return "👤";
      case "role_removed":
        return "🚫";
      case "dm_approval_requested":
        return "📋";
      case "dm_approval_approved":
        return "✅";
      case "dm_approval_rejected":
        return "❌";
      case "admin_character_update":
        return "⚙️";
      default:
        return "📋";
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">System Logs</h2>
        <section className="env-section">
          <div className="env-section-header">SYSTEM ACTIVITY</div>
          <div className="env-loading-state">
            <div className="loading-spinner"></div>
            <p>Loading system logs...</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="env-page-container">
      <h2 className="env-page-title">System Logs</h2>

      {/* Filters Section */}
      <section className="env-section">
        <div className="env-section-header">FILTERS</div>
        <div className="env-filters-grid">
          {/* Log Types */}
          <div className="env-filter-group">
            <label className="env-filter-label">Log Types:</label>
            <div className="env-checkbox-group">
              {logTypeOptions.map((option) => (
                <label key={option.value} className="env-checkbox-label">
                  <input
                    type="checkbox"
                    checked={filters.logTypes.includes(option.value)}
                    onChange={() => handleLogTypeToggle(option.value)}
                    className="env-checkbox"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="env-filter-group">
            <label className="env-filter-label">Start Date:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              className="env-input"
            />
          </div>

          <div className="env-filter-group">
            <label className="env-filter-label">End Date:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              className="env-input"
            />
          </div>

          {/* User/Character Filters */}
          <div className="env-filter-group">
            <label className="env-filter-label">User ID:</label>
            <input
              type="text"
              value={filters.userId}
              onChange={(e) => handleFilterChange("userId", e.target.value)}
              placeholder="Filter by user ID"
              className="env-input"
            />
          </div>

          <div className="env-filter-group">
            <label className="env-filter-label">Character ID:</label>
            <input
              type="text"
              value={filters.characterId}
              onChange={(e) =>
                handleFilterChange("characterId", e.target.value)
              }
              placeholder="Filter by character ID"
              className="env-input"
            />
          </div>

          <div className="env-filter-group">
            <label className="env-filter-label">Results Limit:</label>
            <select
              value={filters.limit}
              onChange={(e) =>
                handleFilterChange("limit", parseInt(e.target.value))
              }
              className="env-select"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>
      </section>

      {/* Logs Section */}
      <section className="env-section">
        <div className="env-section-header">
          SYSTEM ACTIVITY ({totalLogs} total logs)
        </div>

        {error && (
          <div className="env-error-state">
            <p>Error: {error}</p>
          </div>
        )}

        {logs.length === 0 && !loading ? (
          <div className="env-empty-state">
            <p>No logs found matching the current filters.</p>
          </div>
        ) : (
          <div className="env-logs-container">
            {logs.map((log) => (
              <div key={log.id} className="env-log-entry">
                <div className="env-log-header">
                  <span className="env-log-icon">
                    {getActivityIcon(log.type)}
                  </span>
                  <span className="env-log-type">
                    {getLogTypeLabel(log.collection)}
                  </span>
                  <span className="env-log-timestamp">
                    {formatTimestamp(log.timestamp)}
                  </span>
                </div>

                <div className="env-log-content">
                  <div className="env-log-description">{log.description}</div>

                  <div className="env-log-details">
                    {log.userName && (
                      <span className="env-log-detail">
                        <strong>User:</strong> {log.userName} ({log.userEmail})
                      </span>
                    )}
                    {log.characterName && (
                      <span className="env-log-detail">
                        <strong>Character:</strong> {log.characterName}
                        {log.playerName && ` (${log.playerName})`}
                      </span>
                    )}
                    {log.details && Object.keys(log.details).length > 0 && (
                      <span className="env-log-detail">
                        <strong>Details:</strong> {JSON.stringify(log.details)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {hasMore && (
              <div className="env-load-more">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="env-button env-button-secondary"
                >
                  {loading ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default SystemLogs;
